/**
 * Lightweight SVG sparkline + mini candles — no chart.js dependency.
 * Pure presentational; pass closes or OHLC from klines.
 */
export type Ohlc = { open: number; high: number; low: number; close: number };

function extent(values: number[]): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (const v of values) {
    if (!Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { min: 0, max: 1 };
  if (min === max) return { min: min * 0.99, max: max * 1.01 };
  return { min, max };
}

export function Sparkline({
  values,
  width = 280,
  height = 72,
  stroke = 'url(#tcSparkGrad)',
  fill = true,
  className = '',
}: {
  values: number[];
  width?: number;
  height?: number;
  stroke?: string;
  fill?: boolean;
  className?: string;
}) {
  const pts = values.filter((v) => Number.isFinite(v));
  if (pts.length < 2) {
    return <div className={`tc-mob-chart tc-mob-chart--empty ${className}`}>No series</div>;
  }

  const pad = 4;
  const { min, max } = extent(pts);
  const span = max - min || 1;
  const w = width - pad * 2;
  const h = height - pad * 2;

  const coords = pts.map((v, i) => {
    const x = pad + (i / (pts.length - 1)) * w;
    const y = pad + (1 - (v - min) / span) * h;
    return [x, y] as const;
  });

  const line = coords.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area =
    line +
    ` L${coords[coords.length - 1][0].toFixed(1)},${(height - pad).toFixed(1)}` +
    ` L${coords[0][0].toFixed(1)},${(height - pad).toFixed(1)} Z`;

  const up = pts[pts.length - 1] >= pts[0];

  return (
    <svg
      className={`tc-mob-chart tc-mob-chart--spark ${className}`}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-label="Price sparkline"
    >
      <defs>
        <linearGradient id="tcSparkGrad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={up ? '#34d399' : '#fb7185'} stopOpacity="0.55" />
          <stop offset="100%" stopColor={up ? '#6366f1' : '#f43f5e'} />
        </linearGradient>
        <linearGradient id="tcSparkFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={up ? '#34d399' : '#fb7185'} stopOpacity="0.28" />
          <stop offset="100%" stopColor={up ? '#34d399' : '#fb7185'} stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* grid */}
      <line x1={pad} y1={height / 2} x2={width - pad} y2={height / 2} className="tc-mob-chart__grid" />
      {fill && <path d={area} fill="url(#tcSparkFill)" />}
      <path d={line} fill="none" stroke={stroke} strokeWidth="2.2" strokeLinejoin="round" strokeLinecap="round" />
      {/* last point glow */}
      <circle
        cx={coords[coords.length - 1][0]}
        cy={coords[coords.length - 1][1]}
        r="3.5"
        className={up ? 'tc-mob-chart__dot tc-mob-chart__dot--up' : 'tc-mob-chart__dot tc-mob-chart__dot--dn'}
      />
    </svg>
  );
}

export function MiniCandles({
  candles,
  width = 280,
  height = 88,
  className = '',
}: {
  candles: Ohlc[];
  width?: number;
  height?: number;
  className?: string;
}) {
  const list = candles.filter((c) => Number.isFinite(c.close));
  if (list.length < 2) {
    return <div className={`tc-mob-chart tc-mob-chart--empty ${className}`}>No candles</div>;
  }

  const padX = 6;
  const padY = 6;
  const highs = list.map((c) => c.high);
  const lows = list.map((c) => c.low);
  const { min, max } = extent([...highs, ...lows]);
  const span = max - min || 1;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const slot = innerW / list.length;
  const bodyW = Math.max(2, Math.min(8, slot * 0.55));

  const y = (v: number) => padY + (1 - (v - min) / span) * innerH;

  return (
    <svg
      className={`tc-mob-chart tc-mob-chart--candles ${className}`}
      viewBox={`0 0 ${width} ${height}`}
      width="100%"
      height={height}
      preserveAspectRatio="none"
      role="img"
      aria-label="Mini candlestick chart"
    >
      <defs>
        <linearGradient id="tcGrid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(99,102,241,0.08)" />
          <stop offset="100%" stopColor="rgba(99,102,241,0)" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width={width} height={height} fill="url(#tcGrid)" />
      {list.map((c, i) => {
        const cx = padX + slot * i + slot / 2;
        const up = c.close >= c.open;
        const top = y(Math.max(c.open, c.close));
        const bot = y(Math.min(c.open, c.close));
        const bodyH = Math.max(1.5, bot - top);
        const color = up ? '#34d399' : '#fb7185';
        return (
          <g key={i}>
            <line
              x1={cx}
              x2={cx}
              y1={y(c.high)}
              y2={y(c.low)}
              stroke={color}
              strokeWidth="1.4"
              strokeLinecap="round"
              opacity="0.85"
            />
            <rect
              x={cx - bodyW / 2}
              y={top}
              width={bodyW}
              height={bodyH}
              rx="1"
              fill={color}
              opacity="0.92"
            />
          </g>
        );
      })}
    </svg>
  );
}
